import React, { useState, useRef, useEffect } from "react";
import {
  Card,
  CardContent,
  Collapse,
  Grid,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Box,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useContentMetadata } from "../hooks/useCustomQuery";
import CloseIcon from "@mui/icons-material/Close";
import TheaterComedyIcon from "@mui/icons-material/TheaterComedy";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import LanguageIcon from "@mui/icons-material/Language";
import TheatersIcon from "@mui/icons-material/Theaters";
import { getYear } from "../utils/utils";
import useResizeObserver from "../hooks/useResizeObserver";
import PosterTile from "./PosterTile";
import ContentCardActions from "./ContentCardActions";
import WatchProviders from "./WatchProviders";
import { ExpandableTypography } from "./ExpandableTypography";

const languageNames = new Intl.DisplayNames(["en"], { type: "language" });

function MovieCard({
  contentType,
  contentId,
  onClose,
  status,
  onStatusChange,
  fullscreen,
  statusMenuOpenSignal, // forwarded to ContentCardActions so ContentJournal's
  // empty-state link can open the status dropdown (see LibraryModal)
  collapsed = false, // mobile only: shrinks to the poster + title/director/year
  // (see LibraryModal) when the tab content below has been scrolled
}) {
  const { data: metadata, isLoading } = useContentMetadata(
    contentType,
    contentId,
  );
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isDirectorOverflowing, setIsDirectorOverflowing] = useState(false);

  const directorRef = useRef(null);
  const { width: directorWidth, height: directorHeight } =
    useResizeObserver(directorRef);
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (directorRef.current) {
        setIsDirectorOverflowing(
          directorRef.current.scrollWidth > directorRef.current.offsetWidth,
        );
      }
    }, 0);
    return () => clearTimeout(timeout);
  }, [directorWidth, directorHeight, metadata?.persons]);

  const hours = Math.floor(metadata?.details?.runtime / 60);
  const minutes = metadata?.details?.runtime % 60;
  const originalLanguage = metadata?.details?.original_language
    ? languageNames.of(metadata?.details?.original_language)
    : "?";

  const loadingDiv = (
    <Stack direction="column" spacing={1}>
      <Skeleton width="70%" />
      <Skeleton width="60%" />
      <Skeleton width="50%" />
    </Stack>
  );

  const titleDirectorYearDiv = (
    <Stack
      direction="row"
      justifyContent="space-between"
      spacing={1}
      alignItems="flex-start"
      sx={{ pr: 2 }}
    >
      <Box
        sx={{
          "& > span": {
            marginRight: 2,
          },
        }}
      >
        <Typography
          variant="h6"
          component="span"
          sx={{
            fontWeight: "bold",
            display: "inline-block",
          }}
        >
          {metadata?.title}
        </Typography>
        <Box
          component="span"
          sx={{
            whiteSpace: "nowrap",
          }}
        >
          <Box sx={{ display: "inline-flex" }}>
            <Tooltip
              // Conditionally display tooltip with full name of director in the event it becomes clipped due to overflow
              title={metadata?.persons?.join(", ")}
              disableHoverListener={!isDirectorOverflowing}
              placement="bottom-start"
              slotProps={{
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -14],
                      },
                    },
                  ],
                },
              }}
            >
              <Typography
                ref={directorRef}
                variant="subtitle1"
                component="span"
                sx={{
                  marginRight: 2,
                  maxWidth: {
                    xs: "160px",
                    sm: "260px",
                    md: "360px",
                  },
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {metadata?.persons?.join(", ")}
              </Typography>
            </Tooltip>
          </Box>
          <Typography
            variant="subtitle1"
            component="span"
            color="text.secondary"
            sx={{ display: "inline-block" }}
          >
            <i>{getYear(metadata?.details?.release_date)}</i>
          </Typography>
        </Box>
      </Box>
    </Stack>
  );

  const movieStatsDiv = (
    <Box
      sx={{
        flexWrap: "wrap",
        display: "flex",
        direction: "row",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ mr: 1 }}>
        <AccessTimeFilledIcon fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          {metadata?.details?.runtime ? `${hours}h ${minutes}m` : "Unknown"}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mr: 1 }}>
        <TheatersIcon fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          {metadata?.details?.certification
            ? metadata?.details?.certification
            : "Unknown"}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={1}>
        <LanguageIcon fontSize="small" />
        <Typography variant="body2" color="text.secondary">
          {metadata?.details?.original_language ? originalLanguage : "Unknown"}
        </Typography>
      </Stack>
    </Box>
  );

  const genresDiv =
    metadata?.genres?.length > 0 ? (
      <Stack direction="row" spacing={1} alignItems="flex-start">
        <Box sx={{ pt: "6px" }}>
          <TheaterComedyIcon fontSize="small" />
        </Box>
        <Box
          sx={{
            flexWrap: "wrap",
          }}
        >
          {metadata?.genres?.map((genre) => (
            <Chip
              key={genre}
              label={genre.toLowerCase()}
              size="small"
              variant="filled"
              sx={{
                "&.MuiChip-root": {
                  mt: 0.5,
                  mb: 0.5,
                  mr: 1,
                },
              }}
            />
          ))}
        </Box>
      </Stack>
    ) : null;

  return (
    <>
      <Card
        sx={{
          display: "flex",
          position: "relative",
          width: "100%",
          boxShadow: fullscreen ? "none" : "auto",
        }}
      >
        <CardContent
          sx={{
            padding: 2,
            width: "100%",
            "&:last-child": {
              paddingBottom: 2,
            },
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={3}>
              <PosterTile
                contentId={contentId}
                contentType={contentType}
                contentTitle={metadata?.title}
                imageUrl={metadata?.image_url}
                inline
              />
              {isMobile && (
                <Collapse in={!collapsed}>
                  <Box sx={{ mt: 1 }}>
                    <WatchProviders
                      providers={metadata?.watch_providers}
                      checked={metadata?.watch_providers_checked}
                      isMobile
                    />
                  </Box>
                </Collapse>
              )}
            </Grid>
            <Grid
              item
              xs={9}
              position="relative"
              display="flex"
              flexDirection="column"
            >
              {isLoading ? (
                loadingDiv
              ) : (
                <>
                  <Box flexGrow={1}>
                    <Stack direction="column" spacing={1}>
                      {titleDirectorYearDiv}
                      {/* Everything but the title/director/year collapses on
                          mobile once the tab content below is scrolled — see
                          the `collapsed` prop and LibraryModal. Unmounted once
                          hidden, so the overview's Show more resets to
                          collapsed when the card expands again. */}
                      <Collapse in={!collapsed} unmountOnExit>
                        <Stack direction="column" spacing={1}>
                          {movieStatsDiv}
                          {genresDiv}
                          <ExpandableTypography text={metadata?.overview} />
                        </Stack>
                      </Collapse>
                    </Stack>
                    <Tooltip title="Close">
                      <IconButton
                        onClick={onClose}
                        sx={{
                          position: "absolute",
                          top: 16,
                          right: 0,
                          p: 0.5,
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>

                  <Collapse in={!collapsed}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      justifyContent="space-between"
                      sx={{ mt: 2 }}
                    >
                      {isMobile ? (
                        <Box />
                      ) : (
                        <WatchProviders
                          providers={metadata?.watch_providers}
                          checked={metadata?.watch_providers_checked}
                        />
                      )}
                      <ContentCardActions
                        status={status}
                        onStatusChange={onStatusChange}
                        contentId={contentId}
                        contentType={contentType}
                        openSignal={statusMenuOpenSignal}
                      />
                    </Stack>
                  </Collapse>
                </>
              )}
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
}

export default MovieCard;
